import { CloseOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { observer } from 'mobx-react-lite';
import { useState, type ComponentProps } from 'react';

import { withAnalyticId, withAnalyticsNamespace } from '../../utilities/analytics/eventDelegation';
import { AnalyticsNamespace } from '../../utilities/analytics/namespaces';
import { ANALYTIC_ID_NEW_RELEASE_DISMISS, ANALYTIC_ID_NEW_RELEASE_RELOAD } from '../../utils/analytics.ids';
import {
	NewReleaseNotificationContainer,
	NewReleaseNotificationDescription,
	NewReleaseNotificationIcon,
	NewReleaseNotificationText,
	NewReleaseNotificationTitle,
} from './NewReleaseNotification.styled';
import { NEW_RELEASE_NOTIFICATION_MICRO_COPY, type INewReleaseNotificationModel } from './NewReleaseNotificationModel';

export interface NewReleaseNotificationProps extends Omit<
	ComponentProps<typeof NewReleaseNotificationContainer>,
	'children'
> {
	readonly newReleaseNotificationModel: INewReleaseNotificationModel;
	readonly isCompact: boolean;
}

export const NewReleaseNotification = observer(function NewReleaseNotification({
	newReleaseNotificationModel,
	isCompact,
	...props
}: NewReleaseNotificationProps) {
	const { isNewReleaseAvailable, reload } = newReleaseNotificationModel;
	const [isDismissed, setIsDismissed] = useState(false);

	if (!isNewReleaseAvailable || isDismissed) {
		return null;
	}

	function dismiss(): void {
		setIsDismissed(true);
	}

	const reloadButtonProps = {
		...withAnalyticId(ANALYTIC_ID_NEW_RELEASE_RELOAD),
		type: 'primary',
		size: isCompact ? 'small' : 'middle',
		onClick: reload,
		children: NEW_RELEASE_NOTIFICATION_MICRO_COPY.RELOAD,
	} as const satisfies ComponentProps<typeof Button>;

	const dismissButtonProps = {
		...withAnalyticId(ANALYTIC_ID_NEW_RELEASE_DISMISS),
		type: 'text',
		size: 'small',
		icon: <CloseOutlined />,
		onClick: dismiss,
		'aria-label': NEW_RELEASE_NOTIFICATION_MICRO_COPY.DISMISS,
	} as const satisfies ComponentProps<typeof Button>;

	return (
		<NewReleaseNotificationContainer
			{...{
				...withAnalyticsNamespace(AnalyticsNamespace.NewReleaseNotification),
				role: 'status',
				'data-compact': isCompact,
				...props,
			}}
		>
			<NewReleaseNotificationIcon />
			<NewReleaseNotificationText>
				<NewReleaseNotificationTitle>{NEW_RELEASE_NOTIFICATION_MICRO_COPY.TITLE}</NewReleaseNotificationTitle>
				{!isCompact && (
					<NewReleaseNotificationDescription>
						{NEW_RELEASE_NOTIFICATION_MICRO_COPY.DESCRIPTION}
					</NewReleaseNotificationDescription>
				)}
			</NewReleaseNotificationText>
			<Button {...reloadButtonProps} />
			<Button {...dismissButtonProps} />
		</NewReleaseNotificationContainer>
	);
});
